import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Tabs, Tab,
  IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Avatar, Chip, Select, MenuItem, FormControl, InputLabel,
  Card, CardContent, TextField, Switch, Badge, Divider, 
  useMediaQuery, ThemeProvider, createTheme
} from '@mui/material';
import { 
  Delete, Edit, Verified, Block, CheckCircle, 
  MonetizationOn, PeopleAlt, LocalOffer, Store,
  BarChart, CreditCard, Receipt, Settings
} from '@mui/icons-material';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00ffaa',
    },
    secondary: {
      main: '#0088ff',
    },
    background: {
      default: '#0a1929',
      paper: '#001e3c',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", sans-serif',
  },
});

const AdminPage = () => {
  const [tab, setTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [ads, setAds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteType, setDeleteType] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [editFormData, setEditFormData] = useState({});
  const [stats, setStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    adSpotsSold: 0,
    newUsers: 0
  });
  const isMobile = useMediaQuery('(max-width:768px)');

  useEffect(() => {
    fetchAllData();
    // Mock stats - replace with actual data from your backend
    setStats({
      totalRevenue: 12540,
      monthlyRevenue: 3240,
      adSpotsSold: 856,
      newUsers: 142
    });
  }, []);

  const fetchAllData = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const allUsers = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const dealerUsers = allUsers.filter(user => (user.userType || '').toLowerCase() === 'dealer');
      const privateUsers = allUsers.filter(user => (user.userType || '').toLowerCase() === 'private');
      setUsers(privateUsers);
      setDealers(dealerUsers);

      const adsSnapshot = await getDocs(collection(db, 'ads'));
      const allAds = adsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAds(allAds);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const handleDeleteClick = (item, type) => {
    setSelectedItem(item);
    setDeleteType(type);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (item, type) => {
    setSelectedItem(item);
    setEditFormData(item);
    setDeleteType(type);
    setEditDialogOpen(true);
  };

  const handleBlockUser = async (userId, isBlocked) => {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { isBlocked: !isBlocked });
    fetchAllData();
  };

  const confirmDelete = async () => {
    if (!selectedItem || !deleteType) return;
    try {
      const collectionName = deleteType === 'user' ? 'users' : deleteType === 'dealer' ? 'users' : 'ads';
      await deleteDoc(doc(db, collectionName, selectedItem.id));
      fetchAllData();
    } catch (error) {
      console.error("Error deleting:", error);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      setDeleteType('');
    }
  };

  const saveEdit = async () => {
    if (!selectedItem || !deleteType) return;
    try {
      const collectionName = deleteType === 'user' ? 'users' : deleteType === 'dealer' ? 'users' : 'ads';
      const itemRef = doc(db, collectionName, selectedItem.id);
      await updateDoc(itemRef, editFormData);
      fetchAllData();
    } catch (error) {
      console.error("Error updating:", error);
    } finally {
      setEditDialogOpen(false);
      setSelectedItem(null);
      setDeleteType('');
    }
  };

  const handlePlanChange = async (dealerId, newPlan) => {
    const dealerRef = doc(db, 'users', dealerId);
    await updateDoc(dealerRef, { plan: newPlan });
    fetchAllData();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getDealerAdCount = (dealerEmail) => {
    return ads.filter(ad => ad.userEmail === dealerEmail).length;
  };

  const getUserAdCount = (userId) => {
    return ads.filter(ad => ad.userId === userId).length;
  };

  const filteredDealers = filterPlan === 'all' 
    ? dealers 
    : dealers.filter(d => d.plan === filterPlan);

  const filteredUsers = userStatusFilter === 'all' 
    ? users 
    : userStatusFilter === 'paid' 
      ? users.filter(u => u.hasPaid) 
      : users.filter(u => u.isBlocked);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('mk-MK', { style: 'currency', currency: 'MKD' }).format(amount);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
        {/* Sidebar */}
        <Box sx={{ 
          width: isMobile ? 72 : 240, 
          bgcolor: 'background.paper', 
          p: isMobile ? 1 : 3,
          borderRight: '1px solid rgba(255, 255, 255, 0.12)',
          transition: 'all 0.3s ease'
        }}>
          <Typography variant="h6" gutterBottom sx={{ 
            fontWeight: 600, 
            display: isMobile ? 'none' : 'block',
            color: 'primary.main'
          }}>
            AutoVision Admin
          </Typography>
          <Tabs
            orientation="vertical"
            value={tab}
            onChange={(e, newValue) => setTab(newValue)}
            textColor="inherit"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                alignItems: 'flex-start',
                minHeight: 48,
                justifyContent: isMobile ? 'center' : 'flex-start',
                fontSize: isMobile ? '0.7rem' : '0.875rem'
              }
            }}
          >
            <Tab icon={isMobile ? <BarChart /> : null} label={isMobile ? null : "Статистика"} />
            <Tab icon={isMobile ? <PeopleAlt /> : null} label={isMobile ? null : "Корисници"} />
            <Tab icon={isMobile ? <Store /> : null} label={isMobile ? null : "Авто Салони"} />
            <Tab icon={isMobile ? <LocalOffer /> : null} label={isMobile ? null : "Огласи"} />
          </Tabs>
        </Box>

        {/* Main Content */}
        <Box sx={{ 
          flex: 1, 
          p: isMobile ? 2 : 4,
          background: 'linear-gradient(135deg, rgba(10,25,41,0.9) 0%, rgba(0,30,60,0.95) 100%)'
        }}>
          {tab === 0 && (
            <Box>
              <Typography variant="h4" sx={{ 
                mb: 3, 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #00ffaa 30%, #0088ff 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Dashboard Overview
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(0,255,170,0.1) 0%, rgba(0,136,255,0.1) 100%)',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <MonetizationOn color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">Total Revenue</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatCurrency(stats.totalRevenue)}</Typography>
                    <Typography variant="caption" color="text.secondary">All time</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(0,255,170,0.1) 0%, rgba(0,136,255,0.1) 100%)',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Receipt color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">Monthly Revenue</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatCurrency(stats.monthlyRevenue)}</Typography>
                    <Typography variant="caption" color="text.secondary">This month</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(0,255,170,0.1) 0%, rgba(0,136,255,0.1) 100%)',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocalOffer color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">Ad Spots</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{stats.adSpotsSold}</Typography>
                    <Typography variant="caption" color="text.secondary">Sold</Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ 
                    p: 2, 
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(0,255,170,0.1) 0%, rgba(0,136,255,0.1) 100%)',
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PeopleAlt color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle2">New Users</Typography>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{stats.newUsers}</Typography>
                    <Typography variant="caption" color="text.secondary">This month</Typography>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Quick Stats</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography color="text.secondary">Total Users</Typography>
                      <Typography fontWeight={700}>{users.length}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography color="text.secondary">Total Dealers</Typography>
                      <Typography fontWeight={700}>{dealers.length}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography color="text.secondary">Total Ads</Typography>
                      <Typography fontWeight={700}>{ads.length}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography color="text.secondary">Active Premium</Typography>
                      <Typography fontWeight={700}>{dealers.filter(d => d.plan === 'premium').length}</Typography>
                    </Box>
                  </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Card sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Recent Activity</Typography>
                    <Box sx={{ 
                      height: 200, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'text.secondary'
                    }}>
                      Activity chart or recent logs would go here
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 3
              }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #00ffaa 30%, #0088ff 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  User Management
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Filter</InputLabel>
                  <Select 
                    value={userStatusFilter} 
                    label="Filter"
                    onChange={(e) => setUserStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="paid">Paid Users</MenuItem>
                    <MenuItem value="blocked">Blocked Users</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Paper sx={{ 
                p: 2, 
                borderRadius: 3,
                background: 'rgba(0, 30, 60, 0.5)',
                backdropFilter: 'blur(10px)'
              }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>}
                      {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>}
                      <TableCell sx={{ fontWeight: 600 }}>Ads</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map(user => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={user.photoURL} />
                            <Box>
                              <Typography>{user.name}</Typography>
                              {isMobile && (
                                <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        {!isMobile && <TableCell>{user.email}</TableCell>}
                        {!isMobile && <TableCell>
                          <Chip 
                            label={user.userType} 
                            size="small" 
                            color={user.userType === 'private' ? 'secondary' : 'primary'}
                          />
                        </TableCell>}
                        <TableCell>
                          <Badge 
                            badgeContent={getUserAdCount(user.id)} 
                            color="primary"
                            sx={{ '& .MuiBadge-badge': { right: -5, top: -5 } }}
                          />
                        </TableCell>
                        <TableCell>
                          {user.plan ? (
                            <Chip 
                              label={user.plan} 
                              size="small"
                              color={
                                user.plan === 'premium' ? 'primary' : 
                                user.plan === 'basic' ? 'secondary' : 'default'
                              }
                            />
                          ) : (
                            <Typography variant="caption">None</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.hasPaid ? (
                            <Chip 
                              icon={<CheckCircle fontSize="small" />}
                              label="Paid" 
                              size="small"
                              color="success"
                            />
                          ) : user.isBlocked ? (
                            <Chip 
                              icon={<Block fontSize="small" />}
                              label="Blocked" 
                              size="small"
                              color="error"
                            />
                          ) : (
                            <Chip 
                              label="Active" 
                              size="small"
                              color="default"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton 
                              color="primary" 
                              size="small"
                              onClick={() => handleEditClick(user, 'user')}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton 
                              color="error" 
                              size="small"
                              onClick={() => handleDeleteClick(user, 'user')}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                            <IconButton 
                              color={user.isBlocked ? "success" : "warning"} 
                              size="small"
                              onClick={() => handleBlockUser(user.id, user.isBlocked)}
                            >
                              <Block fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}

          {tab === 2 && (
            <Box>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 3
              }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #00ffaa 30%, #0088ff 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Dealer Management
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Filter Plan</InputLabel>
                  <Select 
                    value={filterPlan} 
                    label="Filter Plan"
                    onChange={(e) => setFilterPlan(e.target.value)}
                  >
                    <MenuItem value="all">All Dealers</MenuItem>
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="basic">Basic</MenuItem>
                    <MenuItem value="premium">Premium</MenuItem>
                    <MenuItem value="recomended">Recommended</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              
              <Paper sx={{ 
                p: 2, 
                borderRadius: 3,
                background: 'rgba(0, 30, 60, 0.5)',
                backdropFilter: 'blur(10px)'
              }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Dealer</TableCell>
                      {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>}
                      <TableCell sx={{ fontWeight: 600 }}>Ads</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Plan</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Verified</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDealers.map(dealer => (
                      <TableRow key={dealer.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar src={dealer.logoUrl} />
                            <Box>
                              <Typography>{dealer.dealerName || dealer.name}</Typography>
                              {isMobile && (
                                <Typography variant="caption" color="text.secondary">
                                  {dealer.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        {!isMobile && <TableCell>
                          <Box>
                            <Typography>{dealer.email}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {dealer.phone || '-'}
                            </Typography>
                          </Box>
                        </TableCell>}
                        <TableCell>
                          <Badge 
                            badgeContent={getDealerAdCount(dealer.email)} 
                            color="primary"
                            sx={{ '& .MuiBadge-badge': { right: -5, top: -5 } }}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            value={dealer.plan || 'free'}
                            onChange={(e) => handlePlanChange(dealer.id, e.target.value)}
                            sx={{
                              minWidth: 100,
                              '& .MuiSelect-select': {
                                py: 1,
                                backgroundColor: 
                                  dealer.plan === 'premium' ? 'rgba(0, 255, 170, 0.1)' :
                                  dealer.plan === 'basic' ? 'rgba(0, 136, 255, 0.1)' :
                                  'transparent'
                              }
                            }}
                          >
                            <MenuItem value="free">Free</MenuItem>
                            <MenuItem value="basic">Basic</MenuItem>
                            <MenuItem value="premium">Premium</MenuItem>
                            <MenuItem value="recomended">Recommended</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {['premium', 'recomended'].includes(dealer.plan) ? (
                            <Chip 
                              icon={<Verified fontSize="small" />}
                              label="Verified" 
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip 
                              label="Standard" 
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {dealer.hasPaid ? (
                            <Chip 
                              icon={<CheckCircle fontSize="small" />}
                              label="Paid" 
                              size="small"
                              color="success"
                            />
                          ) : dealer.isBlocked ? (
                            <Chip 
                              icon={<Block fontSize="small" />}
                              label="Blocked" 
                              size="small"
                              color="error"
                            />
                          ) : (
                            <Chip 
                              label="Active" 
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton 
                              color="primary" 
                              size="small"
                              onClick={() => handleEditClick(dealer, 'dealer')}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton 
                              color="error" 
                              size="small"
                              onClick={() => handleDeleteClick(dealer, 'dealer')}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                            <IconButton 
                              color={dealer.isBlocked ? "success" : "warning"} 
                              size="small"
                              onClick={() => handleBlockUser(dealer.id, dealer.isBlocked)}
                            >
                              <Block fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}

          {tab === 3 && (
            <Box>
              <Typography variant="h4" sx={{ 
                mb: 3,
                fontWeight: 700,
                background: 'linear-gradient(45deg, #00ffaa 30%, #0088ff 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Ad Management
              </Typography>
              
              <Paper sx={{ 
                p: 2, 
                borderRadius: 3,
                background: 'rgba(0, 30, 60, 0.5)',
                backdropFilter: 'blur(10px)'
              }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Ad Title</TableCell>
                      {!isMobile && <TableCell sx={{ fontWeight: 600 }}>User</TableCell>}
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Featured</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ads.map(ad => (
                      <TableRow key={ad.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              variant="square" 
                              src={ad.images?.[0]} 
                              sx={{ width: 40, height: 30 }}
                            />
                            <Typography sx={{ 
                              maxWidth: isMobile ? 120 : 200, 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {ad.title || ad.model || 'Ad'}
                            </Typography>
                          </Box>
                        </TableCell>
                        {!isMobile && <TableCell>
                          <Typography>{ad.userName || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ad.userType || '-'}
                          </Typography>
                        </TableCell>}
                        <TableCell>
                          <Chip 
                            label={ad.carType || ad.adType || '-'} 
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={ad.status || 'unknown'} 
                            size="small"
                            color={
                              ad.status === 'active' ? 'success' : 
                              ad.status === 'pending' ? 'warning' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Switch 
                            checked={ad.isFeatured || false}
                            color="primary"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton 
                              color="primary" 
                              size="small"
                              onClick={() => handleEditClick(ad, 'ad')}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton 
                              color="error" 
                              size="small"
                              onClick={() => handleDeleteClick(ad, 'ad')}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}

          {/* Delete Confirmation Dialog */}
          <Dialog 
            open={deleteDialogOpen} 
            onClose={() => setDeleteDialogOpen(false)}
            PaperProps={{
              sx: {
                background: 'linear-gradient(135deg, rgba(0,30,60,0.95) 0%, rgba(10,25,41,0.95) 100%)',
                borderRadius: 3,
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
              }
            }}
          >
            <DialogTitle sx={{ fontWeight: 600 }}>Confirm Deletion</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete this {deleteType}?</Typography>
              {selectedItem && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {deleteType === 'user' ? selectedItem.name : 
                   deleteType === 'dealer' ? selectedItem.dealerName || selectedItem.name : 
                   selectedItem.title || selectedItem.model || 'Ad'}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setDeleteDialogOpen(false)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmDelete} 
                color="error" 
                variant="contained"
                sx={{
                  background: 'linear-gradient(45deg, #ff5555 30%, #ff0000 90%)',
                  boxShadow: '0 3px 5px 2px rgba(255, 0, 0, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #ff3333 30%, #cc0000 90%)'
                  }
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog 
            open={editDialogOpen} 
            onClose={() => setEditDialogOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                background: 'linear-gradient(135deg, rgba(0,30,60,0.95) 0%, rgba(10,25,41,0.95) 100%)',
                borderRadius: 3,
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)'
              }
            }}
          >
            <DialogTitle sx={{ fontWeight: 600 }}>
              Edit {deleteType === 'user' ? 'User' : deleteType === 'dealer' ? 'Dealer' : 'Ad'}
            </DialogTitle>
            <DialogContent>
              {editFormData && (
                <Box sx={{ mt: 2 }}>
                  {deleteType === 'dealer' && (
                    <>
                      <TextField
                        fullWidth
                        label="Dealer Name"
                        name="dealerName"
                        value={editFormData.dealerName || ''}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Slug"
                        name="slug"
                        value={editFormData.slug || ''}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Logo URL"
                        name="logoUrl"
                        value={editFormData.logoUrl || ''}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                      />
                    </>
                  )}
                  {deleteType === 'user' && (
                    <>
                      <TextField
                        fullWidth
                        label="Name"
                        name="name"
                        value={editFormData.name || ''}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel>Plan</InputLabel>
                          <Select
                            name="plan"
                            value={editFormData.plan || 'free'}
                            label="Plan"
                            onChange={handleInputChange}
                          >
                            <MenuItem value="free">Free</MenuItem>
                            <MenuItem value="basic">Basic</MenuItem>
                            <MenuItem value="premium">Premium</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select
                            name="isBlocked"
                            value={editFormData.isBlocked ? 'blocked' : 'active'}
                            label="Status"
                            onChange={(e) => handleInputChange({
                              target: { name: 'isBlocked', value: e.target.value === 'blocked' }
                            })}
                          >
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="blocked">Blocked</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </>
                  )}
                  {deleteType === 'ad' && (
                    <>
                      <TextField
                        fullWidth
                        label="Title"
                        name="title"
                        value={editFormData.title || ''}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                      />
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select
                            name="status"
                            value={editFormData.status || 'pending'}
                            label="Status"
                            onChange={handleInputChange}
                          >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="expired">Expired</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel>Featured</InputLabel>
                          <Select
                            name="isFeatured"
                            value={editFormData.isFeatured || false}
                            label="Featured"
                            onChange={(e) => handleInputChange({
                              target: { name: 'isFeatured', value: e.target.value === 'true' }
                            })}
                          >
                            <MenuItem value={true}>Yes</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setEditDialogOpen(false)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={saveEdit} 
                color="primary" 
                variant="contained"
                sx={{
                  background: 'linear-gradient(45deg, #00ffaa 30%, #0088ff 90%)',
                  boxShadow: '0 3px 5px 2px rgba(0, 136, 255, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #00cc88 30%, #0066cc 90%)'
                  }
                }}
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AdminPage;